/*
 * medicalRecordAccessService.ts — Patient-side authorized medical-record access.
 *
 * Mirrors PortalApi MedicalRecordAccessController. Lets the patient grant a
 * trusted person (family, friend) read access to their records via an i-code +
 * password the recipient enters at the AuthorizedAccess login page.
 *
 * API returns camelCase JSON (Newtonsoft + DefaultContractResolver), so all
 * interface fields here are camelCase regardless of the C# casing.
 */

import { api } from '@/lib/axios';

export interface MedicalRecordAccessInvite {
  medicalRecordAccessKey: number;
  firstName: string;
  lastName: string;
  inviteEmailAddress: string;
  validationCode: string | null;
  expiryDate: string | null;       // ISO date or null
  neverExpire: boolean;
  notes: string | null;
  createdDate: string;
  patientRelationCode: string;
  active: boolean;
  userKey: number | null;
  status: 'Expired' | 'Authorized' | 'InviteSent';
}

export interface CreateInviteRequest {
  firstName: string;
  lastName: string;
  email: string;
  password?: string | null;        // optional — invite works via i-code alone
  expiryDate: string | null;       // ISO date "YYYY-MM-DD" or null
  neverExpire: boolean;
  notes?: string | null;
  patientRelationCode: string;
}

export interface UpdateInviteRequest {
  firstName: string;
  lastName: string;
  email: string;
  password?: string | null;        // omit/empty unless changing
  expiryDate: string | null;
  neverExpire: boolean;
  notes?: string | null;
  patientRelationCode: string;
}

export interface CreateInviteResponse {
  medicalRecordAccessKey: number;
  validationCode: string;
}

export interface UpdateInviteResponse {
  medicalRecordAccessKey: number;
  newValidationCode: string | null; // populated only if email/password changed
}

/** A patient this signed-in user has been granted delegate access to. */
export interface AccessiblePatient {
  patientKey: number;
  firstName: string;
  lastName: string;
  patientRelationCode: string;
}

/** Shape of AuthenticationService.GenerateAccessToken's UserToken, camelCase over the wire. */
export interface DelegateContextToken {
  token: string;
  userName: string;
  userKey: number;
  guidId: string;
  expiredTime: string;
}

export const medicalRecordAccessService = {
  list: () =>
    api.get<MedicalRecordAccessInvite[]>('/MedicalRecordAccess').then((r) => r.data),

  create: (req: CreateInviteRequest) =>
    api.post<CreateInviteResponse>('/MedicalRecordAccess', req).then((r) => r.data),

  update: (inviteKey: number, req: UpdateInviteRequest) =>
    api.put<UpdateInviteResponse>(`/MedicalRecordAccess/${inviteKey}`, req).then((r) => r.data),

  remove: (inviteKey: number) =>
    api.delete<void>(`/MedicalRecordAccess/${inviteKey}`).then((r) => r.data),

  getAccessiblePatients: () =>
    api.get<AccessiblePatient[]>('/MedicalRecordAccess/AccessiblePatients').then((r) => r.data),

  activatePatientContext: (patientKey: number) =>
    api.post<DelegateContextToken>('/MedicalRecordAccess/ActivatePatientContext', { patientKey }).then((r) => r.data),
};

export const medicalRecordAccessKeys = {
  all: ['medicalRecordAccess'] as const,
  // patientKey (authUserMetaAtom.id) must be part of the key — this list is scoped to
  // whichever patient the caller is currently authenticated as, and while viewing another
  // patient's records as an authorized delegate that id changes without a page reload.
  // Without it here, the cached home-patient invite list would keep showing after switching.
  list: (patientKey?: string | null) => [...medicalRecordAccessKeys.all, 'list', patientKey] as const,
};
